import { MantineColorsTuple, MantineThemeOverride } from '@mantine/core'

type CSSVariableString = Record<string, `var(--gm-${string})`>
type ThemeDesignTokens = Omit<MantineThemeOverride, 'colors' | 'spacing'> & {
    colorPrimitives: Record<string, MantineColorsTuple>
    spacing: Record<string, number>
    colors: {
        background: CSSVariableString
        foreground: CSSVariableString
    }
    gradients: {
        genai: string
        genaiOnboarding: string
        genaiText: string
        genaiTextOnDark: string
        highlightText: string
        shimmer: string
    }
}

export const tokens: ThemeDesignTokens = {
    colorPrimitives: {
        gray: [
            'var(--gm-gray-0)',
            'var(--gm-gray-1)',
            'var(--gm-gray-2)',
            'var(--gm-gray-3)',
            'var(--gm-gray-4)',
            'var(--gm-gray-5)',
            'var(--gm-gray-6)',
            'var(--gm-gray-7)',
            'var(--gm-gray-8)',
            'var(--gm-gray-9)',
        ],
        blue: [
            'var(--gm-blue-0)',
            'var(--gm-blue-1)',
            'var(--gm-blue-2)',
            'var(--gm-blue-3)',
            'var(--gm-blue-4)',
            'var(--gm-blue-5)',
            'var(--gm-blue-6)',
            'var(--gm-blue-7)',
            'var(--gm-blue-8)',
            'var(--gm-blue-9)',
        ],
        green: [
            'var(--gm-green-0)',
            'var(--gm-green-1)',
            'var(--gm-green-2)',
            'var(--gm-green-3)',
            'var(--gm-green-4)',
            'var(--gm-green-5)',
            'var(--gm-green-6)',
            'var(--gm-green-7)',
            'var(--gm-green-8)',
            'var(--gm-green-9)',
        ],
        yellow: [
            'var(--gm-yellow-0)',
            'var(--gm-yellow-1)',
            'var(--gm-yellow-2)',
            'var(--gm-yellow-3)',
            'var(--gm-yellow-4)',
            'var(--gm-yellow-5)',
            'var(--gm-yellow-6)',
            'var(--gm-yellow-7)',
            'var(--gm-yellow-8)',
            'var(--gm-yellow-9)',
        ],
        red: [
            'var(--gm-red-0)',
            'var(--gm-red-1)',
            'var(--gm-red-2)',
            'var(--gm-red-3)',
            'var(--gm-red-4)',
            'var(--gm-red-5)',
            'var(--gm-red-6)',
            'var(--gm-red-7)',
            'var(--gm-red-8)',
            'var(--gm-red-9)',
        ],
        lightGray: [
            'var(--gm-gray-0)',
            'var(--gm-gray-0)',
            'var(--gm-gray-0)',
            'var(--gm-gray-0)',
            'var(--gm-gray-0)',
            'var(--gm-gray-2)', // active/pressed state
            'var(--gm-gray-3)', // default state
            'var(--gm-gray-2)', // hover state
            'var(--gm-gray-0)',
            'var(--gm-gray-0)',
        ],
    },
    colors: {
        foreground: {
            'foreground-fg-highlight': 'var(--gm-foreground-fg-highlight)',
            'foreground-fg-primary': 'var(--gm-foreground-fg-primary)',
            'foreground-fg-secondary': 'var(--gm-foreground-fg-secondary)',
            'foreground-fg-tertiary': 'var(--gm-foreground-fg-tertiary)',
            'foreground-fg-disabled': 'var(--gm-foreground-fg-disabled)',
            'foreground-fg-placeholder': 'var(--gm-foreground-fg-placeholder)',
            'foreground-fg-inverse': 'var(--gm-foreground-fg-inverse)',
            'foreground-fg-positive': 'var(--gm-foreground-fg-positive)',
            'foreground-fg-neutral': 'var(--gm-foreground-fg-neutral)',
            'foreground-fg-negative': 'var(--gm-foreground-fg-negative)',
            'foreground-fg-outline-primary': 'var(--gm-foreground-fg-outline-primary)',
        },
        background: {
            'background-bg-highlight': 'var(--gm-background-bg-highlight)',
            'background-bg-highlight-container': 'var(--gm-background-bg-highlight-container)',
            'background-bg-solid': 'var(--gm-background-bg-solid)',
            'background-bg-tonal': 'var(--gm-background-bg-tonal)',
            'background-bg-card': 'var(--gm-background-bg-card)',
            'background-bg-surface': 'var(--gm-background-bg-surface)',
            'background-bg-disabled': 'var(--gm-background-bg-disabled)',
            'background-bg-positive': 'var(--gm-background-bg-positive)',
            'background-bg-positive-container': 'var(--gm-background-bg-positive-container)',
            'background-bg-neutral': 'var(--gm-background-bg-neutral)',
            'background-bg-neutral-container': 'var(--gm-background-bg-neutral-container)',
            'background-bg-negative': 'var(--gm-background-bg-negative)',
            'background-bg-negative-container': 'var(--gm-background-bg-negative-container)',
        },
    },
    headings: {
        fontFamily: 'var(--gm-font-family)',
        sizes: {
            // TODO: We need to include letter spacing, but it's not a prop allowed by Mantine
            // Figure out another way to do this
            h1: {
                fontSize: 'var(--gm-heading-h1-font-size)',
                fontWeight: 'var(--gm-heading-h1-font-weight)',
                lineHeight: 'var(--gm-heading-h1-line-height)',
            },
            h2: {
                fontSize: 'var(--gm-heading-h2-font-size)',
                fontWeight: 'var(--gm-heading-h2-font-weight)',
                lineHeight: 'var(--gm-heading-h2-line-height)',
            },
            h3: {
                fontSize: 'var(--gm-heading-h3-font-size)',
                fontWeight: 'var(--gm-heading-h3-font-weight)',
                lineHeight: 'var(--gm-heading-h3-line-height)',
            },
            h4: {
                fontSize: 'var(--gm-heading-h4-font-size)',
                fontWeight: 'var(--gm-heading-h4-font-weight)',
                lineHeight: 'var(--gm-heading-h4-line-height)',
            },
            h5: {
                fontSize: 'var(--gm-heading-h5-font-size)',
                fontWeight: 'var(--gm-heading-h5-font-weight)',
                lineHeight: 'var(--gm-heading-h5-line-height)',
            },
            h6: {
                fontSize: 'var(--gm-heading-h6-font-size)',
                fontWeight: 'var(--gm-heading-h6-font-weight)',
                lineHeight: 'var(--gm-heading-h6-line-height)',
            },
        },
    },
    spacing: {
        'spacing-2': 2,
        'spacing-4': 4,
        'spacing-8': 8,
        'spacing-12': 12,
        'spacing-16': 16,
        'spacing-20': 20,
        'spacing-24': 24,
        'spacing-32': 32,
    },
    gradients: {
        genai: 'linear-gradient(151.11deg, #03041B 29.76%, #14152E 77.22%)',
        genaiOnboarding: 'linear-gradient(151.11deg, #03041B 29.76%, #14152E 77.22%)',
        genaiText: 'linear-gradient(233.1deg, #A9CFFF 7.12%, #ABA5F4 50%, #C291E3 92.88%)',
        genaiTextOnDark: 'linear-gradient(233deg, #A9CFFF 7.12%, #ABA5F4 50%, #C291E3 92.88%)',
        highlightText: 'var(--gm-gradient-highlight-text)',
        shimmer: 'linear-gradient(115deg, var(--gm-shimmer-dark), var(--gm-shimmer-light), var(--gm-shimmer-dark))',
    },
}
